import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import clsx from 'clsx';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import {ArrowForwardOutlined, SaveOutlined, SwapHorizOutlined} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import BoxResumoInscricao from '../../../publica/Page/inscricao/BoxResumoInscricao.jsx';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import useFetch from '../../../../hooks/useFetch.js';
import useCreate from '../../../../hooks/useCreate.js';
import useUpdate from '../../../../hooks/useUpdate.js';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import {validateCPF} from '../../../../utils/index.js';
import {containedButton, textButton, tituloSecao} from '../../../../utils/styles.js';
import {
    URL_ENDERECO_TIPOS,
    URL_ESTADO_CIVIL,
    URL_FORMACOES,
    URL_GRAU_PARENTESCO,
    URL_INSCRICOES,
    URL_INSCRICOES_APOIO_ATENDENTES,
    URL_INSCRICOES_APOIO_DEFICIENCIAS,
    URL_INSCRICOES_APOIO_PROCESSO,
    URL_INSCRICOES_APOIO_PROCESSOS,
    URL_INSCRICOES_APOIO_PS_INSCRICOES,
    URL_INSCRICOES_APOIO_ENEM_RESULTADO,
    URL_INSCRICOES_APOIO_TIPOS_BOLSA,
    URL_INSCRICOES_CARGA_PROUNI,
    URL_INSCRICOES_ENEM_INEP,
    URL_INSCRICOES_ID,
    URL_INSCRICOES_PESSOA_DADOS,
    URL_INSCRICOES_PESSOAS,
    URL_NACIONALIDADE,
    URL_PROCESSO_SELETIVO_ENEM,
    URL_PROCESSO_SELETIVO_VAGAS,
    URL_RACA,
    URL_TIPO_HABITACAO,
    URL_TIPO_MORADIA
} from '../../../../constants/index.js';
import {
    ehMenorDeIdade,
    estadoInicial,
    exibirResponsavelLegal,
    montarEstadoInicial,
    montarPayload,
    regrasDoPrograma
} from '../../components/inscricao/inscricaoAdministrativaPayload.js';
import {
    ETAPAS,
    criarSchemaInscricao,
    etapasComErro,
    primeiraSecaoComErro,
    secoesComErro
} from '../../components/inscricao/inscricaoAdministrativaSchema.js';
import {montarResumoInscricao} from '../../components/inscricao/resumoInscricaoAdmin.js';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import EtapaDadosPessoais from '../../tabs/inscricao/EtapaDadosPessoais.jsx';
import EtapaSocioeconomico from '../../tabs/inscricao/EtapaSocioeconomico.jsx';
import EtapaDadosInscricao from '../../tabs/inscricao/EtapaDadosInscricao.jsx';
import EtapaDocumentos from '../../tabs/inscricao/EtapaDocumentos.jsx';
import EtapaCalendario from '../../tabs/inscricao/EtapaCalendario.jsx';
import EtapaAnalise from '../../tabs/inscricao/EtapaAnalise.jsx';
import DialogSelecionarPessoa from '../../dialogs/inscricao/DialogSelecionarPessoa.jsx';
import DialogNovaInscricao from '../../dialogs/inscricao/DialogNovaInscricao.jsx';

const ROTA_LISTAGEM = '/administrativo/inscricoes';

const APOIO_VAZIO = {
    processos: [],
    atendentes: [],
    tiposBolsa: [],
    deficiencias: [],
    estadosCivis: [],
    racas: [],
    nacionalidades: [],
    tiposHabitacao: [],
    tiposMoradia: [],
    grausParentesco: [],
    tiposEndereco: [],
    formacoes: []
};

/* Listas de apoio carregadas uma vez: [chave em APOIO_VAZIO, URL]. */
const URLS_APOIO = [
    ['processos', URL_INSCRICOES_APOIO_PROCESSOS],
    ['atendentes', URL_INSCRICOES_APOIO_ATENDENTES],
    ['tiposBolsa', URL_INSCRICOES_APOIO_TIPOS_BOLSA],
    ['deficiencias', URL_INSCRICOES_APOIO_DEFICIENCIAS],
    ['estadosCivis', URL_ESTADO_CIVIL],
    ['racas', URL_RACA],
    ['nacionalidades', URL_NACIONALIDADE],
    ['tiposHabitacao', URL_TIPO_HABITACAO],
    ['tiposMoradia', URL_TIPO_MORADIA],
    ['grausParentesco', URL_GRAU_PARENTESCO],
    ['tiposEndereco', URL_ENDERECO_TIPOS],
    ['formacoes', URL_FORMACOES]
];

const digitos = valor => String(valor ?? '').replace(/\D/g, '');

/* "Curso - Turno - Campus", como a vaga aparece na inscrição pública. */
const descricaoVaga = vaga => [
    vaga?.curso?.descricao || vaga?.curso?.descricaoResumida || '',
    vaga?.turno?.descricao || (vaga?.curso ? 'EAD' : ''),
    vaga?.campus?.nome || ''
].map(item => String(item).trim()).filter(Boolean).join(' - ');


/* Stepper, colunas e rodapé no mesmo estilo do FormInscricao da inscrição pública. */
const useStyles = makeStyles(theme => ({
    stepperRoot: {
        background: 'none',
        border: 'none',
        paddingTop: theme.spacing(),
        paddingRight: 0,
        paddingLeft: 0,
        [theme.breakpoints.up('sm')]: {
            paddingTop: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingLeft: theme.spacing(3)
        }
    },
    contentDiv: {
        marginBottom: theme.spacing(2)
    },
    buttonsGrid: {
        flexDirection: 'column-reverse',
        marginTop: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'initial'
        }
    },
    buttonsDiv: {
        textAlign: 'center',
        [theme.breakpoints.up('sm')]: {
            textAlign: 'right'
        }
    },
    voltarButton: {
        marginLeft: theme.spacing()
    },
    ...containedButton(theme, true, false),
    ...textButton(theme, false),
    containerPaper: {
        padding: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2)
        }
    },
    cardProcesso: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    tituloCard: {
        ...tituloSecao(theme),
        marginBottom: theme.spacing(2)
    },
    itemCard: {
        marginBottom: theme.spacing(2)
    }
}));

/*
 * Criar (/administrativo/inscricoes/novo) e editar (/administrativo/inscricoes/:id)
 * inscrição no administrativo — migração do _form.xhtml do PrimeFaces.
 *
 * Criação: escolhe o processo de seleção (veterano: carrega pelo código do
 * aluno) e o CPF; se já há cadastro com o CPF, escolhe a pessoa e o formulário
 * vem com os dados dela e da última inscrição; sem cadastro, tenta a carga do
 * Prouni. Só então as demais etapas são liberadas.
 *
 * Visual da inscrição pública no desktop: Stepper com as etapas, a etapa com
 * seções verticais à esquerda e o resumo da inscrição à direita. O admin pode
 * ir a qualquer etapa e salvar de qualquer uma (edição); o Salvar valida tudo.
 */
export default function PageInscricao() {
    const classes = useStyles();
    const {id} = useParams();
    const edicao = !!id;
    const navigate = useNavigate();
    const checkModule = useCheckModule();
    const permitido = checkModule('EDITINSCRI');
    const podeEditarNome = checkModule('EDITNOME');
    const podeEditarDataNascimento = checkModule('EDITDTNASC');

    const [fetchInscricao, carregandoInscricao] = useFetch({returnInnerLoadingState: true});
    const [fetchApoio] = useFetch({returnInnerLoadingState: true});
    const fetch = useFetch();
    const create = useCreate();
    const update = useUpdate();
    const [solicitar, solicitandoInep] = useCreate({returnInnerLoadingState: true});
    const {createToast} = useToast();
    const confirmation = useConfirmationDialog();
    const processoRequisitado = useRef(null);
    // Resultado do INEP que veio com a inscrição ({chave: 'ano|cpf', resultado}); evita buscá-lo ao abrir.
    const resultadoEnemInscricao = useRef(null);

    const [apoio, setApoio] = useState(APOIO_VAZIO);
    const [carregandoApoio, setCarregandoApoio] = useState(false);
    // Processo, vagas e anos do ENEM do processo escolhido (depois da inscrição, na edição).
    const [carregandoProcesso, setCarregandoProcesso] = useState(false);
    const [processo, setProcesso] = useState(null);
    const [vagas, setVagas] = useState([]);
    const [enems, setEnems] = useState([]);
    // Resultado do INEP (notas por área e média) do ano do ENEM escolhido para o CPF informado.
    const [resultadoEnem, setResultadoEnem] = useState(null);
    const [carregandoResultadoEnem, setCarregandoResultadoEnem] = useState(false);
    // Ano do filtro do resultado do INEP; acompanha o ano do ENEM do formulário, mas pode ser trocado.
    const [anoInep, setAnoInep] = useState(null);
    const [psInscricoes, setPsInscricoes] = useState([]);
    const [original, setOriginal] = useState(null);
    const [cpfConsultado, setCpfConsultado] = useState('');
    const [alunoCarregado, setAlunoCarregado] = useState(false);
    const [pessoasEncontradas, setPessoasEncontradas] = useState([]);
    const [dialogPessoaAberto, setDialogPessoaAberto] = useState(false);
    const [etapa, setEtapa] = useState(0);
    // Seção a abrir na etapa atual (depois de um Salvar com erro); a chave força o efeito mesmo na mesma seção.
    const [secaoFoco, setSecaoFoco] = useState(null);
    // Criação: o processo (e o código do aluno, no veterano) é escolhido no diálogo antes do formulário.
    const [dialogNovaAberto, setDialogNovaAberto] = useState(!id);

    const schema = useMemo(() => criarSchemaInscricao(), []);
    const contextoValidacao = useRef({});

    const {control, handleSubmit, setValue, getValues, watch, reset, setError, clearErrors, formState: {errors, isSubmitting, isDirty}} = useForm({
        defaultValues: estadoInicial(),
        resolver: (valores, contexto, opcoes) => yupResolver(schema)(valores, contextoValidacao.current, opcoes)
    });

    const idProcessoSeletivo = watch('idProcessoSeletivo');
    const pessoaId = watch('pessoa.id');
    const cpfDigitos = digitos(watch('pessoa.cpf'));
    const dataNascimento = watch('pessoa.dataNascimento');
    const idEnem = watch('inscricaoEnem.processoSeletivoEnem');
    const anoEnem = enems.find(item => String(item.id) === String(idEnem))?.enem?.anoEnem || null;

    useEffect(() => setAnoInep(anoEnem), [anoEnem]);

    const processoResumo = apoio.processos.find(item => String(item.id) === String(idProcessoSeletivo))
        || (original?.processoSeletivo && String(original.processoSeletivo.id) === String(idProcessoSeletivo) ? original.processoSeletivo : null);
    const processoAtual = processo && String(processo.id) === String(idProcessoSeletivo) ? processo : null;
    const programaId = processoAtual?.programa?.id ?? processoResumo?.programa?.id ?? null;
    const regras = regrasDoPrograma(programaId, processoAtual || {});
    const temResponsavelCadastrado = !!original?.pessoa?.responsavelAtual?.responsavel?.id;
    const exibirResponsavel = exibirResponsavelLegal(dataNascimento, temResponsavelCadastrado);
    const menorDeIdade = ehMenorDeIdade(dataNascimento);
    const cpfResolvido = edicao || !!pessoaId || (cpfDigitos.length === 11 && cpfDigitos === cpfConsultado);
    // Criação: processo escolhido no diálogo (e, no veterano, aluno carregado); ao trocar, o formulário fica atrás do diálogo.
    const processoEscolhido = !!idProcessoSeletivo && (!regras.exibirCodigoAluno || alunoCarregado);
    const mostrarAbas = edicao ? (!!idProcessoSeletivo && !!original) : processoEscolhido;
    // Loading da página pelo LoadingModal global: inscrição, listas de apoio e dados do processo.
    useCarregamentoPagina(carregandoInscricao || carregandoApoio || carregandoProcesso || carregandoResultadoEnem || solicitandoInep);

    contextoValidacao.current = {programaId, exigirCategoria: !!processoAtual?.exigirCategoria, temResponsavelCadastrado};

    /* Processo, vagas e anos do ENEM; respostas de um processo trocado no meio do caminho são descartadas. */
    const carregarProcesso = idProcesso => {
        processoRequisitado.current = idProcesso ? String(idProcesso) : null;
        if (!idProcesso) {
            setProcesso(null);
            setVagas([]);
            setEnems([]);
            setCarregandoProcesso(false);
            return;
        }
        const ehAtual = () => processoRequisitado.current === String(idProcesso);
        const atual = callback => resposta => ehAtual() && callback(resposta);
        setCarregandoProcesso(true);
        Promise.all([
            fetchApoio(URL_INSCRICOES_APOIO_PROCESSO(idProcesso), {}, atual(setProcesso)),
            fetchApoio(URL_PROCESSO_SELETIVO_VAGAS(idProcesso), {}, atual(lista => setVagas(lista || []))),
            fetchApoio(URL_PROCESSO_SELETIVO_ENEM(idProcesso), {}, atual(lista => setEnems(lista || [])))
        ]).finally(() => ehAtual() && setCarregandoProcesso(false));
    };

    useEffect(() => {
        if (!permitido) return;
        setCarregandoApoio(true);
        Promise.all(URLS_APOIO.map(([, url]) => fetchApoio(url, {})))
            .then(respostas => setApoio(URLS_APOIO.reduce((listas, [chave], indice) => ({
                ...listas,
                [chave]: respostas[indice] || []
            }), {...APOIO_VAZIO})))
            .finally(() => setCarregandoApoio(false));
    }, []);

    /* Troca de rota (/novo ↔ /:id) reaproveita o componente: recomeça o formulário do zero antes de carregar. */
    useEffect(() => {
        if (!permitido) return;
        resultadoEnemInscricao.current = null;
        reset(estadoInicial());
        setOriginal(null);
        setEtapa(0);
        setSecaoFoco(null);
        setCpfConsultado('');
        setAlunoCarregado(false);
        carregarProcesso(null);
        setDialogNovaAberto(!edicao);
        if (!edicao) return;
        fetchInscricao(URL_INSCRICOES_ID(id), {}, vo => {
            resultadoEnemInscricao.current = vo?.resultadoEnem
                ? {chave: `${vo.resultadoEnem.numAno}|${digitos(vo.pessoa?.cpf)}`, resultado: vo.resultadoEnem}
                : null;
            setOriginal(vo);
            reset(montarEstadoInicial(vo));
            carregarProcesso(vo?.processoSeletivo?.id);
        });
    }, [id]);

    /* Inscrições no processo seletivo da pessoa (só para quem já tem cadastro). */
    useEffect(() => {
        if (!permitido || !pessoaId || !idProcessoSeletivo || !regras.exibirPsInscricao) {
            setPsInscricoes([]);
            return;
        }
        fetchApoio(URL_INSCRICOES_APOIO_PS_INSCRICOES, {idPessoa: pessoaId, idProcessoSeletivo}, lista => setPsInscricoes(lista || []));
    }, [pessoaId, idProcessoSeletivo, regras.exibirPsInscricao]);

    /* Resultado do INEP do ano do filtro, pelo CPF da inscrição: na abertura usa o que veio com a inscrição e só busca quando o ano ou o CPF mudam. */
    useEffect(() => {
        let ativo = true;
        setResultadoEnem(null);
        if (!permitido || !regras.exibirEnem || !anoInep || cpfDigitos?.length !== 11) return;
        if (resultadoEnemInscricao.current?.chave === `${anoInep}|${cpfDigitos}`) {
            setResultadoEnem(resultadoEnemInscricao.current.resultado);
            return;
        }
        setCarregandoResultadoEnem(true);
        fetchApoio(URL_INSCRICOES_APOIO_ENEM_RESULTADO, {ano: anoInep, cpf: cpfDigitos}, resultado => ativo && setResultadoEnem(resultado || null))
            .finally(() => ativo && setCarregandoResultadoEnem(false));
        // Respostas fora de ordem (ano ou CPF trocado no meio do caminho) são descartadas.
        return () => {
            ativo = false;
            setCarregandoResultadoEnem(false);
        };
    }, [anoInep, cpfDigitos, regras.exibirEnem]);

    /* Na criação, a inscrição no PS copiada de outra ficha só fica se for deste processo. */
    useEffect(() => {
        const atual = getValues('psInscricao');
        if (!edicao && atual && !psInscricoes.some(item => String(item.id) === String(atual))) {
            setValue('psInscricao', '');
        }
    }, [psInscricoes]);

    /* Pré-preenche o formulário com um VO (pessoa existente, carga do Prouni ou aluno), mantendo processo e CPF. */
    const aplicarPreenchimento = (vo, {codigoAluno} = {}) => {
        const atual = getValues();
        const novo = montarEstadoInicial(vo);
        reset({
            ...novo,
            id: null,
            idProcessoSeletivo: atual.idProcessoSeletivo,
            codigoAluno: codigoAluno ?? atual.codigoAluno,
            pessoa: {...novo.pessoa, cpf: novo.pessoa.cpf || atual.pessoa.cpf},
            vaga: vagas.some(vaga => String(vaga.id) === novo.vaga) ? novo.vaga : ''
        }, {keepDefaultValues: true});
        setOriginal({...vo, id: null});
    };

    const consultarCpf = cpf => {
        fetch(URL_INSCRICOES_PESSOAS, {cpf}, pessoas => {
            if (pessoas && pessoas.length) {
                setPessoasEncontradas(pessoas);
                setDialogPessoaAberto(true);
                return;
            }
            fetch(URL_INSCRICOES_CARGA_PROUNI, {cpf, idProcessoSeletivo}, vo => {
                if (vo) aplicarPreenchimento(vo);
                setCpfConsultado(cpf);
            }, (erro, tratarErro) => {
                tratarErro(erro);
                setCpfConsultado(cpf);
            });
        });
    };

    /* CPF completo na criação de pessoa nova: procura cadastro existente (ou a carga do Prouni). */
    useEffect(() => {
        if (!permitido || edicao || pessoaId || cpfDigitos.length !== 11 || cpfDigitos === cpfConsultado) return;
        if (!validateCPF(cpfDigitos)) {
            setError('pessoa.cpf', {type: 'manual', message: 'CPF inválido'});
            return;
        }
        clearErrors('pessoa.cpf');
        consultarCpf(cpfDigitos);
    }, [cpfDigitos]);

    const selecionarPessoa = pessoa => {
        fetch(URL_INSCRICOES_PESSOA_DADOS(pessoa.idPessoa), {idProcessoSeletivo}, vo => {
            setDialogPessoaAberto(false);
            aplicarPreenchimento(vo);
            setCpfConsultado(cpfDigitos);
        }, (erro, tratarErro) => {
            // 400 quando a pessoa já está inscrita no processo: mostra a mensagem e volta para o CPF.
            tratarErro(erro);
            fecharDialogPessoa();
        });
    };

    /* Sem escolher um cadastro existente, o CPF volta em branco: não se cria outra pessoa com o mesmo CPF. */
    const fecharDialogPessoa = () => {
        setDialogPessoaAberto(false);
        setValue('pessoa.cpf', '');
    };

    /*
     * Continuar do diálogo de nova inscrição: o formulário recomeça com o
     * processo escolhido e, no veterano, já vem com os dados do aluno.
     */
    const iniciarInscricao = ({idProcessoSeletivo: idProcesso, codigoAluno, voAluno}) => {
        reset({...estadoInicial(), idProcessoSeletivo: String(idProcesso), codigoAluno: codigoAluno || ''});
        setOriginal(null);
        setEtapa(0);
        setSecaoFoco(null);
        setCpfConsultado('');
        setAlunoCarregado(false);
        carregarProcesso(idProcesso);
        if (voAluno) {
            aplicarPreenchimento(voAluno, {codigoAluno});
            setAlunoCarregado(true);
            setCpfConsultado(digitos(voAluno.pessoa?.cpf));
        }
        setDialogNovaAberto(false);
    };

    /* Fechar o diálogo sem processo escolhido volta para a lista; trocando de processo, mantém o atual. */
    const fecharDialogNova = () => {
        if (processoEscolhido) {
            setDialogNovaAberto(false);
            return;
        }
        navigate(ROTA_LISTAGEM);
    };

    const trocarProcesso = () => {
        if (!isDirty) {
            setDialogNovaAberto(true);
            return;
        }
        confirmation('Trocar o processo de seleção descarta os dados informados na inscrição. Deseja continuar?', () => setDialogNovaAberto(true));
    };

    const salvar = valores => {
        const payload = montarPayload(valores, {
            original,
            processo: processoAtual || processoResumo,
            temResponsavelCadastrado
        });
        const concluir = mensagem => () => {
            createToast({severity: 'success', message: mensagem});
            navigate(ROTA_LISTAGEM);
        };
        return edicao
            ? update(URL_INSCRICOES_ID(id), payload, concluir('Inscrição atualizada com sucesso!'))
            : create(URL_INSCRICOES, payload, concluir('Inscrição criada com sucesso!'));
    };


    /* Pede o resultado do ENEM ao INEP para o ano escolhido; a resposta substitui o resultado mostrado. */
    const solicitarInep = ano => {
        solicitar(URL_INSCRICOES_ENEM_INEP(id), {ano}, resposta => {
            resultadoEnemInscricao.current = null;
            setResultadoEnem(resposta || null);
            const situacao = resposta?.situacaoPedido;
            const message = situacao === 'ATENDIDO' ? 'Resultado recebido do INEP.'
                : situacao === 'NAO_ENCONTRADO' ? 'O INEP não encontrou resultado para este ano e CPF.'
                    : 'Pedido enviado ao INEP. O resultado ainda não está disponível.';
            createToast({severity: 'success', message});
        });
    };

    const irParaEtapa = indice => {
        setSecaoFoco(null);
        setEtapa(indice);
    };

    /* Salvar com erro: vai para a etapa e a seção do primeiro erro (sem CPF resolvido, para o CPF). */
    const aoInvalidar = erros => {
        // Processo ou código do aluno inválido (só na criação): volta ao diálogo que os escolhe.
        if (!edicao && (erros.idProcessoSeletivo || erros.codigoAluno)) {
            setDialogNovaAberto(true);
            return;
        }
        const primeiro = cpfResolvido ? primeiraSecaoComErro(erros) : {etapa: 0, secao: 'dadosPessoais'};
        if (!primeiro) return;
        setEtapa(primeiro.etapa);
        setSecaoFoco({secao: primeiro.secao, chave: Date.now()});
    };

    const cancelar = () => {
        if (!isDirty) {
            navigate(ROTA_LISTAGEM);
            return;
        }
        confirmation('Há alterações não salvas. Deseja descartá-las?', () => navigate(ROTA_LISTAGEM));
    };

    if (!permitido) {
        return <PageNaoEncontrada/>;
    }

    // Processo mostrado na coluna da direita (somente leitura): o detalhe, o da lista ou o da inscrição.
    const processoExibido = processoAtual || processoResumo || original?.processoSeletivo || null;

    const vagaAtual = original?.vaga;
    const opcoesVaga = vagas.map(vaga => ({valor: String(vaga.id), descricao: descricaoVaga(vaga)}));
    if (edicao && vagaAtual?.id && !opcoesVaga.some(opcao => opcao.valor === String(vagaAtual.id))) {
        opcoesVaga.unshift({valor: String(vagaAtual.id), descricao: descricaoVaga(vagaAtual) || `Vaga ${vagaAtual.id}`});
    }

    const psAtual = original?.psInscricao;
    const opcoesPsInscricao = psInscricoes.map(item => ({valor: String(item.id), descricao: item.descricao}));
    if (edicao && psAtual?.id && !opcoesPsInscricao.some(opcao => opcao.valor === String(psAtual.id))) {
        opcoesPsInscricao.unshift({valor: String(psAtual.id), descricao: `Inscrição ${psAtual.id}`});
    }

    const valores = watch();
    const resumo = montarResumoInscricao(valores, {opcoesVaga});
    const errosPorSecao = secoesComErro(errors);
    const errosPorEtapa = etapasComErro(errors);
    const pessoaExistente = !!pessoaId;
    const ultimaEtapa = etapa === ETAPAS.length - 1;
    const props = {control, errors, watch, setValue, secoesComErro: errosPorSecao, secaoFoco};

    const conteudoEtapa = () => {
        switch (ETAPAS[etapa].id) {
            case 'socioeconomico':
                return (
                    <EtapaSocioeconomico
                        {...props}
                        regras={regras}
                        tiposHabitacao={apoio.tiposHabitacao}
                        tiposMoradia={apoio.tiposMoradia}
                        grausParentesco={apoio.grausParentesco}
                        tiposEndereco={apoio.tiposEndereco}/>
                );
            case 'inscricao':
                return (
                    <EtapaDadosInscricao
                        {...props}
                        regras={regras}
                        vagas={opcoesVaga}
                        psInscricoes={opcoesPsInscricao}
                        enems={enems}
                        anoEnem={anoEnem}
                        anoInep={anoInep}
                        onAlterarAnoInep={setAnoInep}
                        resultadoEnem={resultadoEnem}
                        onSolicitarInep={edicao ? solicitarInep : undefined}
                        pontuacaoProcessoSeletivo={original?.pontuacaoProcessoSeletivo ?? null}
                        redacaoProcessoSeletivo={original?.redacaoProcessoSeletivo ?? null}
                        formacoes={apoio.formacoes}
                        dataAtualizacaoPSInscricao={original?.dataAtualizacaoPSInscricao || null}/>
                );
            case 'documentacao':
                return <EtapaDocumentos {...props} documentosProcesso={processoAtual?.documentos || []}/>;
            case 'calendario':
                return <EtapaCalendario {...props} tiposCalendario={processoAtual?.tiposCalendario || []}/>;
            case 'avaliacao':
                return (
                    <EtapaAnalise
                        {...props}
                        tiposBolsa={apoio.tiposBolsa}
                        exibirDivida={regras.exibirDivida}
                        atendentes={apoio.atendentes}/>
                );
            default:
                return (
                    <EtapaDadosPessoais
                        {...props}
                        cpfTravado={edicao || pessoaExistente}
                        cpfResolvido={cpfResolvido}
                        nomeTravado={pessoaExistente && !(edicao && podeEditarNome)}
                        dataNascimentoTravada={pessoaExistente && !(edicao && podeEditarDataNascimento)}
                        nomeMaeTravado={pessoaExistente && !!original?.pessoa?.nomeMae}
                        exibirResponsavel={exibirResponsavel}
                        menorDeIdade={menorDeIdade}
                        dataEmancipacaoMaior={!menorDeIdade && original?.pessoa?.emancipada === 'SIM' ? (original.pessoa.dataEmancipacao || null) : null}
                        ultimaAlteracao={original?.ultimaAlteracao || null}
                        estadosCivis={apoio.estadosCivis}
                        racas={apoio.racas}
                        nacionalidades={apoio.nacionalidades}
                        deficiencias={apoio.deficiencias}/>
                );
        }
    };

    const titulo = edicao
        ? `Editar Inscrição - ${id}${original?.pessoa?.nome ? ` / ${original.pessoa.nome}` : ''}`
        : 'Nova Inscrição';

    const botaoSalvar = (
        <Button key='salvar' variant='contained' startIcon={<SaveOutlined/>}
                onClick={handleSubmit(salvar, aoInvalidar)}
                className={classes.containedButton} disabled={isSubmitting}>
            Salvar
        </Button>
    );

    return (
        <>
            <PageWrapper>
                <PageTitle title={titulo}/>
                {/* Salvar só pelo botão: Enter num campo não grava a inscrição inteira. */}
                <form noValidate autoComplete='off' onSubmit={evento => evento.preventDefault()}>
                    <Grid container spacing={3}>
                        {mostrarAbas && (
                            <>
                                <Grid item xs={12}>
                                    <Stepper nonLinear activeStep={etapa} alternativeLabel classes={{root: classes.stepperRoot}}>
                                        {ETAPAS.map((item, indice) => (
                                            <Step key={item.id} completed={!edicao && indice < etapa} disabled={indice > 0 && !cpfResolvido}>
                                                <StepButton onClick={() => irParaEtapa(indice)}>
                                                    <StepLabel error={errosPorEtapa.has(indice)}>{item.label}</StepLabel>
                                                </StepButton>
                                            </Step>
                                        ))}
                                    </Stepper>
                                </Grid>
                                <Grid item xs={12} sm={9}>
                                    <Paper className={classes.containerPaper}>
                                        <div className={classes.contentDiv}>
                                            {conteudoEtapa()}
                                        </div>
                                        <Grid container spacing={3} className={classes.buttonsGrid}>
                                            <Grid item xs={12}>
                                                <Divider/>
                                            </Grid>
                                            <Grid item xs={12} sm={9}>
                                                <div className={classes.buttonsDiv}>
                                                    <Button className={classes.textButton} onClick={cancelar}>Cancelar</Button>
                                                    <Button className={clsx(classes.textButton, classes.voltarButton)}
                                                            onClick={() => irParaEtapa(etapa - 1)} disabled={etapa === 0}>
                                                        Voltar
                                                    </Button>
                                                    {edicao && !ultimaEtapa && (
                                                        <Button className={clsx(classes.textButton, classes.voltarButton)}
                                                                onClick={() => irParaEtapa(etapa + 1)}>
                                                            Avançar
                                                        </Button>
                                                    )}
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                {edicao || ultimaEtapa ? botaoSalvar : (
                                                    <Button key='avancar' variant='contained' startIcon={<ArrowForwardOutlined/>}
                                                            className={classes.containedButton} disabled={!cpfResolvido}
                                                            onClick={() => irParaEtapa(etapa + 1)}>
                                                        Avançar
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Paper elevation={3} className={classes.cardProcesso}>
                                        <Typography component='h4' className={classes.tituloCard}>Processo de Seleção</Typography>
                                        <LabelValue label='Processo' className={classes.itemCard} value={processoExibido?.descricao || 'Não informado'}/>
                                        <LabelValue label='Programa' className={classes.itemCard} value={processoExibido?.programa?.descricao || 'Não informado'}/>
                                        {original?.descricaoProcessoSeletivo && (
                                            <LabelValue label='Processo Seletivo' className={classes.itemCard} value={original.descricaoProcessoSeletivo}/>
                                        )}
                                        {regras.exibirCodigoAluno && (
                                            <LabelValue label='Código do aluno' className={classes.itemCard} value={String(valores.codigoAluno || '') || 'Não informado'}/>
                                        )}
                                        {!edicao && (
                                            <Button className={classes.textButton} startIcon={<SwapHorizOutlined/>} onClick={trocarProcesso}>
                                                Trocar processo
                                            </Button>
                                        )}
                                    </Paper>
                                    <BoxResumoInscricao inscricaoFallback={resumo}/>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </form>
            </PageWrapper>
            <DialogNovaInscricao
                open={dialogNovaAberto}
                onClose={fecharDialogNova}
                processos={apoio.processos}
                valorInicial={processoEscolhido ? {idProcessoSeletivo, codigoAluno: valores.codigoAluno} : null}
                onContinuar={iniciarInscricao}/>
            <DialogSelecionarPessoa
                open={dialogPessoaAberto}
                onClose={fecharDialogPessoa}
                pessoas={pessoasEncontradas}
                onSelecionar={selecionarPessoa}/>
        </>
    );
}
